import { DOCUMENT, Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { isLanguageCode } from '../i18n/languages';
import { environment } from '../../environments/environment';
import {
  AuthTokenReq,
  AuthTokenRes,
  ChangeContactInfoReq,
  ChangeLanguageReq,
  ChangeNameReq,
  CreateAccountReq,
  CreateUserReq,
  IdRes,
  StatusRes,
  UserRes,
} from '../types/rest-api';

/**
 * Service for User-domain REST API endpoints.
 * Maps to OpenAPI tag: User
 * Base path: /v1/users
 */
@Injectable({
  providedIn: 'root',
})
export class UserApiService {
  private readonly http = inject(HttpClient);
  private readonly document = inject(DOCUMENT);
  private readonly baseUrl = `${environment.apiBaseUrl}/v1/users`;

  // POST /v1/users/create-account
  createAccount(body: CreateAccountReq) {
    return this.http.post<IdRes>(`${this.baseUrl}/create-account`, body);
  }

  // POST /v1/users/auth-token
  getAuthToken(body: AuthTokenReq) {
    return this.http.post<AuthTokenRes>(`${this.baseUrl}/auth-token`, body);
  }

  // GET /v1/users/account
  getAccount() {
    return this.http.get<UserRes>(`${this.baseUrl}/account`);
  }

  // DELETE /v1/users/account
  deleteAccount() {
    return this.http.delete<StatusRes>(`${this.baseUrl}/account`);
  }

  // POST /v1/users/create-profile
  // The language the visitor sees becomes the account language (mails too).
  createProfile(body: CreateUserReq) {
    // LanguageService keeps <html lang> on the language shown.
    const current = this.document.documentElement.lang;
    return this.http.post<UserRes>(`${this.baseUrl}/create-profile`, {
      ...body,
      language: body.language ?? (isLanguageCode(current) ? current : null),
    });
  }

  // PUT /v1/users/change-name
  changeName(body: ChangeNameReq) {
    return this.http.put<StatusRes>(`${this.baseUrl}/change-name`, body);
  }

  // PUT /v1/users/change-contact-info
  changeContactInfo(body: ChangeContactInfoReq) {
    return this.http.put<StatusRes>(
      `${this.baseUrl}/change-contact-info`,
      body
    );
  }

  // PUT /v1/users/language
  changeLanguage(body: ChangeLanguageReq) {
    return this.http.put<StatusRes>(`${this.baseUrl}/language`, body);
  }

  // POST /v1/users/profile-picture  (multipart/form-data)
  uploadProfilePicture(file: File) {
    const form = new FormData();
    form.append('file', file);
    return this.http.post<IdRes>(`${this.baseUrl}/profile-picture`, form);
  }

  // DELETE /v1/users/profile-picture
  deleteProfilePicture() {
    return this.http.delete<StatusRes>(`${this.baseUrl}/profile-picture`);
  }

  // POST /v1/users/wall-picture  (multipart/form-data)
  uploadWallPicture(file: File) {
    const form = new FormData();
    form.append('file', file);
    return this.http.post<IdRes>(`${this.baseUrl}/wall-picture`, form);
  }

  // DELETE /v1/users/wall-picture
  deleteWallPicture() {
    return this.http.delete<StatusRes>(`${this.baseUrl}/wall-picture`);
  }
}
